import { Box, Typography } from '@mui/material'
import SearchInput from '../../Common/SearchInput'
import { useAppDispatch, useAppSelector } from '../../../redux/hook'
import {
    EntityInstancePath,
    EntityStep,
    SelectedItemsInRelations,
    updateEntityInstancePath,
    updateEntitySteps,
} from '../../../redux/slices/entityInstanceSlice'
import theme from '../../../theme/theme'
import { CloseOutlined } from '@mui/icons-material'
import {
    fetchEntityRecordsListBySearch,
    modifingItemInRelation,
} from '../../../services/entityServices'
import { v4 as uuidv4 } from 'uuid'
import {
    EntityStepTypes,
    RelationsType,
    UserPermissions,
} from '../../../constants/constants'
import ConfirmationDialog from '../../Common/ConfirmationDialog'
import { ReactElement, useMemo, useState } from 'react'
import { updateLoader } from '../../../redux/slices/loaderSlice'
import InstanceSubTemplatesList from './InstanceSubTemplatesList'
import { useTranslation } from 'react-i18next'
import { findEntityById } from '../../../utils/EntityUtils'
import { EntityType } from '../../DTStudio/interfaces'
import { isDevOrQAEnv } from '../../../utils/MiscellaneousUtils'

const RelationStep = (): ReactElement => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()

    const orgId = useAppSelector(
        (state) => state.entityInstance.startStep.orgId,
    )

    const navList = useAppSelector((state) => state.nav.navList)

    const permissions = useAppSelector((state) => state.org.permissions)

    const path = useAppSelector((state) => state.entityInstance.path)

    const entitySteps = useAppSelector(
        (state) => state.entityInstance.entitySteps,
    )
    const activeEntityStep = useAppSelector(
        (state) => state.entityInstance.activeEntityStep,
    )

    const entityAttributeStep = entitySteps?.find(
        (i) => i.stepType === EntityStepTypes.Attributes,
    )

    const IsInstanceReadOnly = useAppSelector(
        (state) => state.entityInstance.readOnly,
    )

    const [
        isOpenDeleteConfirmForRelationEntity,
        setIsOpenDeleteConfirmForRelationEntity,
    ] = useState<boolean>(false)

    const [deletingRelationItem, setDeletingRelationItem] =
        useState<SelectedItemsInRelations | null>(null)

    const [openSubTemplates, setOpenSubTemplates] = useState(false)

    const templateData: EntityType = useMemo(
        () =>
            findEntityById(navList, entitySteps[activeEntityStep].templateId)
                ?.entity || ({} as EntityType),
        [navList, entitySteps, activeEntityStep],
    )

    //adding new Device has permission only for org admin and device writer only.
    //first we need check is the relation template is device or not. then we need to check is super admin or device manager.
    const isDeviceManager = isDevOrQAEnv()
        ? true
        : templateData?.permissions?.writer?.device
          ? permissions.includes(UserPermissions.DEVICE_WRITER) ||
            permissions.includes(UserPermissions.ORG_ADMIN)
          : true //setting true if not device template

    const getFilterSearchList = () => {
        let updatedReferenceList = entitySteps[activeEntityStep].referenceList
        const copySelectedItems = entitySteps[activeEntityStep].selectedItems
        updatedReferenceList = updatedReferenceList?.filter(
            (i: any) =>
                !copySelectedItems?.some((s: any) => s.entityId === i.entityId),
        )

        return updatedReferenceList
    }

    const onSearchStepsRelationRecords = async (
        entityType: string,
        id: string,
        value: any,
    ) => {
        const entityID = entityType
        const searchText = value
        const parentTemplateID = entityAttributeStep?.templateId

        const relationEntityRecords = await fetchEntityRecordsListBySearch(
            orgId,
            entityID,
            parentTemplateID || '',
            searchText,
        )
        let updateSteps = [...entitySteps]
        updateSteps = updateSteps.map((step: EntityStep) => {
            if (step.stepNo - 1 === activeEntityStep) {
                return {
                    ...step,
                    referenceList: relationEntityRecords
                        ? relationEntityRecords
                        : [],
                }
            } else {
                return step
            }
        })
        dispatch(updateEntitySteps(updateSteps))
    }

    const onSelectRelationRecord = async (item: any) => {
        const parentEntityId = entityAttributeStep?.instanceId
        const payload = {
            parentInstanceId: parentEntityId,
            childInstanceId: item.entityId,
            newChildInstanceId: '',
            relationStatus: 'ACTIVE',
            relationName: entitySteps[activeEntityStep].relationId,
        }

        await modifingItemInRelation(orgId, payload)
        let updateSteps = [...entitySteps]
        updateSteps = updateSteps.map((step: EntityStep) => {
            if (step.stepNo - 1 === activeEntityStep) {
                const selectingItem: SelectedItemsInRelations = {
                    displayName: item.displayName,
                    entityId: item.entityId,
                    templateId: item.templateId,
                    templateName: '',
                    status: '',
                }

                let updateSelectedItems: SelectedItemsInRelations[] = [
                    ...(step.selectedItems || []),
                ]
                updateSelectedItems =
                    step.mapping?.toLowerCase() === RelationsType.ONE_TO_ONE
                        ? [selectingItem]
                        : [...updateSelectedItems, selectingItem]
                return {
                    ...step,
                    selectedItems: updateSelectedItems,
                }
            } else {
                return step
            }
        })
        dispatch(updateEntitySteps(updateSteps))
    }

    const onClickUID = (item: SelectedItemsInRelations) => {
        const uniqueId = uuidv4()
        const pathObj: EntityInstancePath = {
            pathId: uniqueId,
            pathName: entitySteps[activeEntityStep]?.relationName,
            activeStep: 0,
            orgId,
            templateId: item?.templateId,
            templateName: item?.templateName,
            relationId: entitySteps[activeEntityStep]?.relationId,
            relationName: entitySteps[activeEntityStep]?.relationName,
            entityId: item.entityId,
            entityName: '',
            parentInstanceId: entityAttributeStep?.instanceId || '',
            parentValues: {},
        }

        let updatedPath = [...path]
        updatedPath = updatedPath.map((p, i) => {
            if (i + 1 === updatedPath.length) {
                return { ...p, ...{ activeStep: activeEntityStep } } //updating active step in previous path when adding new path. so we can active that step when back to that path
            } else {
                return { ...p }
            }
        })

        updatedPath = [...updatedPath, pathObj]

        dispatch(
            updateEntityInstancePath({
                path: updatedPath,
                isFirst: false,
                hasStartStep: false,
            }),
        )
    }

    const onSubmitSubTemplatePopUp = (templateData: {
        templateId: string
        templateName: string
    }) => {
        setOpenSubTemplates(false)

        const uniqueId = uuidv4()

        const parentValues: { [key: string]: string } = {}
        entityAttributeStep?.attributes?.forEach((i: any) => {
            parentValues[i.id] = i.value
        })

        const pathObj: EntityInstancePath = {
            pathId: uniqueId,
            pathName: entitySteps[activeEntityStep]?.relationName,
            activeStep: 0,
            orgId,
            templateId: templateData?.templateId,
            templateName: templateData?.templateName,
            relationId: entitySteps[activeEntityStep]?.relationId,
            relationName: entitySteps[activeEntityStep]?.relationName,
            entityId: '',
            entityName: '',
            parentInstanceId: entityAttributeStep?.instanceId || '',
            parentValues,
        }

        let updatedPath = [...path]
        updatedPath = updatedPath.map((p, i) => {
            if (i + 1 === updatedPath.length) {
                return { ...p, ...{ activeStep: activeEntityStep } }
            } else {
                return { ...p }
            }
        })

        updatedPath = [...updatedPath, pathObj]

        dispatch(
            updateEntityInstancePath({
                path: updatedPath,
                isFirst: false,
                hasStartStep: false,
            }),
        )
    }

    const onCloseSubTemplatePopUp = () => {
        setOpenSubTemplates(false)
    }

    const onClickDeleteRelationItemAndOpenConfirmation = (
        item: SelectedItemsInRelations,
    ) => {
        setIsOpenDeleteConfirmForRelationEntity(true)
        setDeletingRelationItem(item)
    }
    const onClickDeleteUID = async () => {
        dispatch(updateLoader(true))
        const parentEntityId = entityAttributeStep?.instanceId
        const payload = {
            parentInstanceId: parentEntityId,
            childInstanceId: deletingRelationItem?.entityId,
            newChildInstanceId: '',
            relationStatus: 'DELETED',
        }

        await modifingItemInRelation(orgId, payload)
        let updateSteps = [...entitySteps]
        updateSteps = updateSteps.map((step: EntityStep) => {
            if (step.stepNo - 1 === activeEntityStep) {
                let updateSelectedItems: SelectedItemsInRelations[] = [
                    ...(step.selectedItems || []),
                ]
                updateSelectedItems = updateSelectedItems.filter(
                    (i) => i.entityId !== deletingRelationItem?.entityId,
                )
                return {
                    ...step,
                    selectedItems: updateSelectedItems,
                }
            } else {
                return step
            }
        })
        dispatch(updateEntitySteps(updateSteps))
        setIsOpenDeleteConfirmForRelationEntity(false)
        dispatch(updateLoader(false))
    }

    return (
        <Box
            sx={{
                marginBottom: '20px',
                marginTop: '20px',
            }}
            display="flex"
            alignItems="flex-start"
            flexDirection="column"
        >
            <Box sx={{ marginBottom: '20px' }}>
                <Typography
                    id="entity-instance-dialog-description"
                    sx={{
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#000',
                    }}
                >
                    {entitySteps[activeEntityStep]?.relationName}
                </Typography>
            </Box>
            {IsInstanceReadOnly ? null : (
                <Box
                    sx={{
                        width: '360px',
                        marginBottom: '20px',
                    }}
                    display="flex"
                    alignItems="center"
                >
                    <Box>
                        <SearchInput
                            id={
                                entitySteps[activeEntityStep]?.relationId +
                                '-search'
                            }
                            label=""
                            value=""
                            onChange={(id: string, value: string) =>
                                onSearchStepsRelationRecords(
                                    entitySteps[activeEntityStep].templateId,
                                    id,
                                    value,
                                )
                            }
                            onSelect={(obj: any) => onSelectRelationRecord(obj)}
                            searchedList={getFilterSearchList() || []}
                            isAddNew={isDeviceManager}
                            onSelectAddNew={() => setOpenSubTemplates(true)}
                        />
                    </Box>
                </Box>
            )}
            {entitySteps[activeEntityStep]?.selectedItems?.length ? (
                <Box>
                    <Box>
                        <Typography
                            sx={{
                                fontWeight: '600',
                                fontSize: '14px',
                            }}
                        >
                            {t('selectedItems')}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                        }}
                    >
                        {entitySteps[activeEntityStep]?.selectedItems?.map(
                            (item: SelectedItemsInRelations) => (
                                <Box
                                    sx={{
                                        background:
                                            entitySteps[activeEntityStep]
                                                ?.selectedUID === item?.entityId
                                                ? theme.palette.primary.main
                                                : '#E9E9E9',
                                        padding: '10px',
                                        marginRight: '10px',
                                        borderRadius: '5px',
                                        marginTop: '10px',
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                    key={item.entityId}
                                >
                                    <Box
                                        onClick={() => onClickUID(item)}
                                        id={item.displayName}
                                        sx={{
                                            cursor: 'pointer',
                                            width: `${item.entityId ? 'auto' : '50px'}`,
                                            height: `${item.entityId ? 'auto' : '20px'}`,
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: '14px',
                                                color:
                                                    entitySteps[
                                                        activeEntityStep
                                                    ]?.selectedUID ===
                                                    item.entityId
                                                        ? '#FFF'
                                                        : '#333',
                                            }}
                                        >
                                            {item.displayName}
                                        </Typography>
                                    </Box>
                                    {IsInstanceReadOnly ? null : (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                marginLeft: '6px',
                                                cursor: 'pointer',
                                            }}
                                            id={
                                                item.displayName +
                                                '-delete-icon'
                                            }
                                            onClick={() =>
                                                onClickDeleteRelationItemAndOpenConfirmation(
                                                    item,
                                                )
                                            }
                                        >
                                            {' '}
                                            <CloseOutlined
                                                sx={{
                                                    color:
                                                        entitySteps[
                                                            activeEntityStep
                                                        ]?.selectedUID ===
                                                        item.entityId
                                                            ? '#FFF'
                                                            : '#747474',
                                                    fontSize: '20px',
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            ),
                        )}
                    </Box>
                </Box>
            ) : null}
            <ConfirmationDialog
                openDialog={isOpenDeleteConfirmForRelationEntity}
                handleComfirm={() => onClickDeleteUID()}
                handleClose={() =>
                    setIsOpenDeleteConfirmForRelationEntity(false)
                }
                DialogHeaderText={t('confirmDelete')}
                DialogBodyText={t('confirmDeleteCommon')}
            />
            {openSubTemplates ? (
                <InstanceSubTemplatesList
                    onClose={onCloseSubTemplatePopUp}
                    onSubmit={onSubmitSubTemplatePopUp}
                    orgId={orgId}
                    templateId={entitySteps[activeEntityStep]?.templateId}
                />
            ) : (
                <></>
            )}
        </Box>
    )
}

export default RelationStep
